import {
  Alert,
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { HelpOutline } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNumberFormat } from '../hooks/useNumberFormat';
import { formatDateOnly } from '../utils/date';
import type { SubscriptionUsagePeriodStatus, SubscriptionUsagePeriodSummary } from '../types';

const statusColorMap: Record<SubscriptionUsagePeriodStatus, 'default' | 'info' | 'success'> = {
  open: 'info',
  closed: 'default',
  reconciled: 'success',
};

type SubscriptionUsagePeriodStatusPresentation = 'technical' | 'public';

interface SubscriptionUsagePeriodsTableProps {
  usagePeriods: SubscriptionUsagePeriodSummary[];
  loading?: boolean;
  error?: string | null;
  title: string;
  subtitle?: string;
  emptyMessage: string;
  asCard?: boolean;
  statusPresentation?: SubscriptionUsagePeriodStatusPresentation;
}

function UsageMetricCell({
  primaryLabel,
  primaryValue,
  secondaryLabel,
  secondaryValue,
}: {
  primaryLabel: string;
  primaryValue: string;
  secondaryLabel: string;
  secondaryValue: string;
}) {
  return (
    <Stack spacing={0.5}>
      <Typography variant="body2" fontWeight={600}>
        {primaryLabel}: {primaryValue}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {secondaryLabel}: {secondaryValue}
      </Typography>
    </Stack>
  );
}

function SingleMetricCell({
  primaryValue,
  secondaryLabel,
}: {
  primaryValue: string;
  secondaryLabel?: string;
}) {
  return (
    <Stack spacing={0.35}>
      <Typography variant="body2" fontWeight={600}>
        {primaryValue}
      </Typography>
      {secondaryLabel ? (
        <Typography variant="caption" color="text.secondary">
          {secondaryLabel}
        </Typography>
      ) : null}
    </Stack>
  );
}

function HeaderWithHelp({
  label,
  tooltip,
}: {
  label: string;
  tooltip?: string;
}) {
  if (!tooltip) {
    return <>{label}</>;
  }

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
      <span>{label}</span>
      <Tooltip title={tooltip} arrow>
        <HelpOutline sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
      </Tooltip>
    </Box>
  );
}

export default function SubscriptionUsagePeriodsTable({
  usagePeriods,
  loading = false,
  error,
  title,
  subtitle,
  emptyMessage,
  asCard = true,
  statusPresentation = 'technical',
}: SubscriptionUsagePeriodsTableProps) {
  const { t } = useTranslation(['pages', 'common']);
  const { formatDuration, locale } = useNumberFormat();

  const getStatusChipConfig = (
    status: SubscriptionUsagePeriodStatus
  ): { color: 'default' | 'info' | 'success'; label: string } => {
    if (statusPresentation === 'public') {
      const publicStatus: SubscriptionUsagePeriodStatus = status === 'open' ? 'open' : 'closed';
      return {
        color: publicStatus === 'open' ? 'info' : 'default',
        label: t(`pages:subscription.history.status.${publicStatus}`),
      };
    }

    return {
      color: statusColorMap[status] || 'default',
      label: t(`pages:subscription.history.status.${status}`),
    };
  };

  const content = (
    <>
      {subtitle && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {subtitle}
        </Typography>
      )}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={24} />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : usagePeriods.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {emptyMessage}
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small" sx={{ minWidth: 760 }}>
            <TableHead>
              <TableRow>
                <TableCell>{t('pages:subscription.history.columns.period')}</TableCell>
                <TableCell>{t('pages:subscription.history.columns.status')}</TableCell>
                <TableCell>
                  <HeaderWithHelp
                    label={t('pages:subscription.history.columns.usage')}
                    tooltip={t('pages:subscription.history.tooltips.usageNet')}
                  />
                </TableCell>
                <TableCell>
                  <HeaderWithHelp
                    label={t('pages:subscription.history.columns.refunds')}
                    tooltip={t('pages:subscription.history.tooltips.refunds')}
                  />
                </TableCell>
                <TableCell>{t('pages:subscription.history.columns.overage')}</TableCell>
                <TableCell>
                  <HeaderWithHelp
                    label={t('pages:subscription.history.columns.balance')}
                    tooltip={t('pages:subscription.history.tooltips.balance')}
                  />
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {usagePeriods.map((period) => {
                const remainingPlanSeconds = period.planSecondsIncluded === -1
                  ? -1
                  : Math.max(period.planSecondsIncluded - period.minutesUsedSeconds, 0);

                return (
                  <TableRow key={period.id} hover>
                    <TableCell>
                      <Stack spacing={0.35}>
                        <Typography variant="body2" fontWeight={600}>
                          {formatDateOnly(period.periodStart, { locale })}
                          {' \u2192 '}
                          {formatDateOnly(period.periodEnd, { locale })}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {period.planName}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" {...getStatusChipConfig(period.status)} />
                    </TableCell>
                    <TableCell>
                      <UsageMetricCell
                        primaryLabel={t('pages:subscription.history.labels.ai')}
                        primaryValue={formatDuration(period.aiUsedSeconds)}
                        secondaryLabel={t('pages:subscription.history.labels.telephony')}
                        secondaryValue={formatDuration(period.telephonyUsedSeconds)}
                      />
                    </TableCell>
                    <TableCell>
                      <UsageMetricCell
                        primaryLabel={t('pages:subscription.history.labels.ai')}
                        primaryValue={formatDuration(period.aiRefundedSeconds)}
                        secondaryLabel={t('pages:subscription.history.labels.telephony')}
                        secondaryValue={formatDuration(period.telephonyRefundedSeconds)}
                      />
                    </TableCell>
                    <TableCell>
                      <UsageMetricCell
                        primaryLabel={t('pages:subscription.history.labels.ai')}
                        primaryValue={formatDuration(period.overageAiSeconds)}
                        secondaryLabel={t('pages:subscription.history.labels.telephony')}
                        secondaryValue={formatDuration(period.overageTelephonySeconds)}
                      />
                    </TableCell>
                    <TableCell>
                      <SingleMetricCell
                        primaryValue={
                          remainingPlanSeconds === -1
                            ? t('common:subscription.unlimited')
                            : formatDuration(remainingPlanSeconds)
                        }
                        secondaryLabel={t('pages:subscription.history.balanceCaption')}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  );

  if (!asCard) {
    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={1.5}>
          <Typography variant="subtitle2" fontWeight={700}>
            {title}
          </Typography>
          {content}
        </Stack>
      </Paper>
    );
  }

  return (
    <Card>
      <CardHeader title={title} />
      <CardContent>{content}</CardContent>
    </Card>
  );
}
